import { CloseOutlined, WarningOutlined } from '@ant-design/icons'
import { Checkbox, Tooltip } from 'antd'

import DeliveryTime from '@/components/Atoms/DeliveryTime/DeliveryTime'
import QuantityToggler from '@/components/Cart/QuantityToggler/QuantityToggler'
import UpdatedAtTooltip from '@/components/Feed/UpdatedAtTooltip/UpdatedAtTooltip'
import { getAvailabilityStatusText } from '@/pages/Cart/Cart.config'
import cn from '@/pages/Cart/Cart.module.scss'
import { useCartStore } from '@/stores/cart/cartStore'
import type {
  CartProductInterface,
  OrderStockItemType,
  OrderSupplierStockType,
  StockedProduct,
} from '@/types'
import { trimTrailingZeros } from '@/utils/format/trimTrailingZeros'
import { formatPriceWithVat } from '@/utils/price/vat'
import getProductKey from '@/utils/products/getProductKey'
import {
  formatDeliveryDate,
  getStockItemValue,
} from '@/utils/products/productDataHelpers'

interface CartAndOrderCreationMobileCartProps {
  product: StockedProduct
  deleteById?: (itemId: string) => void
  checked: boolean
  onCheck?: (checked: boolean, product: CartProductInterface) => void
  index?: number
  mode?: 'cart' | 'order'
  allProducts?: StockedProduct[]
  updateProducts?: (newProducts: CartProductInterface[]) => void
  highlightedProductId?: string | null
}

export const CartAndOrderCreationMobile = ({
  product,
  deleteById,
  checked = false,
  onCheck,
  index,
  mode = 'cart',
  updateProducts,
  highlightedProductId,
}: CartAndOrderCreationMobileCartProps) => {
  const { duplicateProductsQuantities } = useCartStore()

  const supplierStock = product.suppliersStock?.[0] as
    | OrderSupplierStockType
    | undefined
  const stockItem = supplierStock?.stock?.[0] as OrderStockItemType | undefined
  const productKey = String(getProductKey(product))
  const cartProduct = 'prices' in product ? product : undefined

  if (!supplierStock || !stockItem) return null

  const availableQuantity = stockItem?.quantity ?? 0
  const quantityInCart =
    mode === 'order'
      ? stockItem.quantity
      : (duplicateProductsQuantities[productKey] ??
        stockItem?.cartInfo?.quantity ??
        stockItem?.quantityInCart ??
        0)

  let optPriceWithVAT = '-'
  let totalOptWithVAT = '-'
  let totalRetailPriceWithVAT = '-'

  if (mode === 'order' && stockItem.total !== undefined) {
    totalOptWithVAT = trimTrailingZeros(stockItem.total)
    optPriceWithVAT = trimTrailingZeros(stockItem.total / stockItem.quantity)

    const retailBase = stockItem.totalRetailPrice ?? stockItem.retailPrice ?? 0
    totalRetailPriceWithVAT = formatPriceWithVat(retailBase)
  } else {
    const basePrice = cartProduct?.prices?.sellingPrice ?? 0

    if (basePrice > 0) {
      optPriceWithVAT = formatPriceWithVat(basePrice)
      totalOptWithVAT = formatPriceWithVat(basePrice * quantityInCart)
    }

    const retailPricePerUnit = cartProduct?.prices?.retailPrice ?? 0
    if (retailPricePerUnit > 0) {
      totalRetailPriceWithVAT = formatPriceWithVat(
        retailPricePerUnit * quantityInCart
      )
    }
  }

  const deliveryTime =
    (mode !== 'order' || !stockItem.deliveryDate) &&
    stockItem.effectiveEtaMinDays !== undefined ? (
      <DeliveryTime
        minDays={stockItem.effectiveEtaMinDays}
        maxDays={stockItem.effectiveEtaMaxDays}
        outOfStock={mode === 'cart' && stockItem.quantity <= 0}
      />
    ) : (
      formatDeliveryDate(product)
    )

  const statusText =
    mode === 'cart' && cartProduct
      ? getAvailabilityStatusText(cartProduct)
      : null
  const isProblematic =
    mode === 'cart' &&
    (statusText !== null ||
      availableQuantity === 0 ||
      quantityInCart > availableQuantity ||
      quantityInCart === 0)
  const isHighlighted = highlightedProductId === productKey
  const store = getStockItemValue(product, 'storeShortModel')

  const cardClasses = [
    cn.cartCard,
    checked ? cn.selected : '',
    isProblematic ? cn.noStockCard : '',
    isHighlighted ? cn.highlightCard : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={cardClasses} id={`row-${productKey}`}>
      {mode === 'cart' && (
        <div className={cn.cardHeader}>
          <Checkbox
            checked={checked}
            onChange={(e) =>
              cartProduct && onCheck?.(e.target.checked, cartProduct)
            }
          />
          <span>
            {cartProduct?.authorShortDto?.name ?? '-'},{' '}
            {cartProduct?.addedToTheCartAt
              ? new Date(cartProduct.addedToTheCartAt).toLocaleDateString()
              : '-'}
          </span>
          <div
            className={cn.deleteButtonInCard}
            onClick={() =>
              store &&
              deleteById?.(stockItem?.cartInfo?.id || cartProduct?.id || '')
            }
          >
            <CloseOutlined />
          </div>
        </div>
      )}

      <div className={cn.cardBody}>
        {mode === 'cart' && (
          <span className={cn.index}>
            № {typeof index !== 'undefined' ? index + 1 : '-'}
          </span>
        )}

        <p>
          <b>Артикул:</b> {product.nomenclatureDto.article ?? '-'}
        </p>
        <p>
          <b>Бренд:</b> {product.nomenclatureDto.brandDto?.description ?? '-'}
        </p>
        <p>
          <b>Наименование:</b> {product.nomenclatureDto.description ?? '-'}
        </p>
        <p>
          <b>Склад:</b> {store?.description ?? '-'}
        </p>
        {statusText && (
          <Tooltip title={statusText} placement="top">
            <div className={cn.availabilityStatusBadge}>
              <WarningOutlined className={cn.availabilityStatusIcon} />
              <span>{statusText}</span>
            </div>
          </Tooltip>
        )}

        <div className={cn.quantityBlock}>
          <span>
            <b>Количество:</b>{' '}
          </span>
          {mode === 'cart' ? (
            availableQuantity === 0 ? (
              <span className={cn.noStockTextInCard}>Нет на складе</span>
            ) : (
              <UpdatedAtTooltip
                updatedAt={stockItem.stockUpdatedAt}
                kind="stock"
              >
                <QuantityToggler
                  location="cart"
                  element={product}
                  itemDesc={
                    product.nomenclatureDto.unitOfMeasurementDto?.description
                  }
                  updateProducts={updateProducts}
                  maxQuantity={availableQuantity}
                />
              </UpdatedAtTooltip>
            )
          ) : (
            <span>
              {quantityInCart}{' '}
              {product.nomenclatureDto.unitOfMeasurementDto?.description}
            </span>
          )}
        </div>

        <p>
          <b>Срок доставки:</b> {deliveryTime}
        </p>
        <p>
          <b>ОПТ:</b>{' '}
          <UpdatedAtTooltip
            updatedAt={cartProduct?.prices.priceUpdatedAt}
            kind="price"
            priceSource={cartProduct?.prices.priceSource}
          >
            {optPriceWithVAT} BYN
          </UpdatedAtTooltip>
        </p>
        <p>
          <b>Сумма ОПТ:</b> {totalOptWithVAT} BYN
        </p>
        <p>
          <b>Сумма РРЦ:</b> {totalRetailPriceWithVAT} BYN
        </p>
      </div>
    </div>
  )
}
